
import { Component, Mixins, Prop } from "vue-property-decorator";
import { ReceptionReference, ReceptionReferenceDispInfo, WorkflowReference, ReceptionDetailPermissions } from "../../../../../domain/workflow/reference";
import { ButtonInfo, WorkflowDestination, ReceptionOperation, ManagerReference, ReceptionTypeItem } from "../../../../../domain/workflow/items";
import { Reception, ReceptionDetail } from "../../../../../domain/workflow/reception";
import { FormatStatusKinds, FormatStatusKindsName, ReceptionCommentKinds , ReceptionCommentKindsName } from "../../../../../domain/workflow/kinds";
import { default as WorkflowActionBaseMixin } from "../../../mixins/WorkflowActionBaseMixin.vue";
import { DocumentSummary, ReferenceFileInformation } from "../../../../../domain/document";
import { FormatReference } from "../../../../../domain/format";
import { DomainRegistry } from "../../../../../domain";

import { Department } from "../../../../../domain/department";
import { ReceptionDispInfo } from "../../../../../domain/workflow/ReceptionDispInfo";

@Component({
})
export default class CommonTabMixin extends Mixins(WorkflowActionBaseMixin) {
  @Prop()
  reference: ReceptionReference;

  @Prop({default: false})
  isReadOnly: boolean;
  
  // --------------プロパティ-------------------------------

  get displayInfo(): ReceptionReferenceDispInfo {
    return this.reference ? this.reference.displayInfo : null;
  }

  get labelSetting(): ReceptionDispInfo {
    return this.displayInfo ? this.displayInfo.labelSetting : null;
  }

  get paymentStatusList(): ReceptionTypeItem[] {
    return this.labelSetting
      ? this.labelSetting.paymentStatusList
      : null;
  }

  get reception(): Reception {
    return this.reference ? this.reference.reception : new Reception();
  }

  get detail(): ReceptionDetail {
    return this.reference ? this.reference.receptionDetail : null;
  }

  get receptionDetailId(): number {
    return this.detail ? this.detail.id : null;
  }

  get formatStatus(): FormatStatusKinds {
    return this.detail ? this.detail.formatStatus : FormatStatusKinds.Available;
  }

  get formatStatusDisplayName(): string {
    return FormatStatusKindsName.getDisplayName(this.formatStatus);
  }

  get permissions(): ReceptionDetailPermissions {
    return this.reference ? this.reference.displayInfo.permissions : null;
  }

  get workflow(): WorkflowReference {
    return this.reference ? this.reference.workflow : null;
  }

  get lastWorkflowHistoryId(): string {
    return this.workflow ? this.workflow.lastHistoryId : null;
  }

  get document(): DocumentSummary {
    return this.reference ? this.reference.document : null;
  }

  get isUnmanaged(): boolean {
    return !!this.document && this.document.isUnmanaged;
  }

  get shouldRenderDocumentPdfByPlugin(): boolean {
    return this.$configuration.behavior.document.viewer.pdfRendering.shouldRenderUnmanagedDocumentByPlugin && this.isUnmanaged;
  }

  get documentReferenceFiles(): ReferenceFileInformation[] {
    return this.reference ? this.reference.documentReferenceFiles : null;
  }

  get requestSheetReferenceFiles(): ReferenceFileInformation[] {
    return this.reference ? this.reference.requestSheetReferenceFiles : null;
  }

  get isExistDocument(): boolean {
    return !!this.document;
  }

  // 無条件に文書参照可能か
  get documentReadable(): boolean {
    return this.isReadable(this.document);
  }

  get quoteSourceDocument(): DocumentSummary {
    return this.reference ? this.reference.quoteSourceDocument : null;
  }

  documentShouldConfirm(document: DocumentSummary): boolean {
    return document ? document.shouldConfirmReadable : false;
  }

  documentShuoldLogin(document: DocumentSummary): boolean {
    return document ? document.shouldLogin : false;
  }

  documentShouldWriteLog(document: DocumentSummary): boolean {
    return document ? document.shouldWriteAuditLog : false;
  }

  get requestSheetFormats(): FormatReference[] {
    return this.reference ? this.reference.requestSheetFormats : [];
  }

  get requestSheetFormatStatus(): FormatStatusKinds {
    return this.reference ? this.reference.requestSheetFormatStatus : FormatStatusKinds.Available;
  }

  get requestSheetFormatStatusDisplayName(): string {
    return FormatStatusKindsName.getDisplayName(this.requestSheetFormatStatus);
  }

  get requestSheetDocument(): DocumentSummary {
    return this.reference ? this.reference.requestSheetDocument : null;
  }

  get isExistRequestSheetDocument(): boolean {
    return !!this.requestSheetDocument;
  }

  get requestSheetDocumentReadable(): boolean {
    return this.isReadable(this.requestSheetDocument);
  }
  
  private isReadable(doc: DocumentSummary): boolean {
    return doc ? doc.isReadable 
      && !doc.shouldConfirmReadable
      && !doc.shouldLogin
      && !doc.shouldWriteAuditLog : false;
  }

  isFormatAvailable(value: FormatStatusKinds): boolean {
    return value === undefined || value === FormatStatusKinds.Available;
  }

  get operationHistories(): ReceptionOperation[] {
    return this.reference ? this.reference.operationHistories : [];
  }

  get managersHistories(): ManagerReference[] {
    return this.reference ? this.reference.managersHistories : [];
  }

  get department(): Department {
    return this.detail ? this.detail.department : null;
  }

  get isMigrated(): boolean {
    return this.reception.isMigrated;
  }

  get receiptFormatOriginalIds(): number[] {
    return this.reference ? this.reference.receiptFormatOriginalIds : [];
  }

  async doWorkflowAction(permission: ButtonInfo, destination: WorkflowDestination, nextMessage: string, backMessage: string,
                         shouldPrintDocument: boolean = false, shouldSelectPrinter: boolean = false, actioned: () => Promise<void> = null,
                         commentKind: ReceptionCommentKinds = null, nextMessageCommentId: number = null, nextMessageArchiveId: number = null): Promise<boolean> {
    try {
      this.startLoading();
      const result = await this.execWorkflowAction(this.receptionDetailId, permission, destination, nextMessage, backMessage, this.lastWorkflowHistoryId, nextMessageCommentId, nextMessageArchiveId);
      if (result.success && shouldPrintDocument) {
        await this.printDocumentWithCommitAction(shouldSelectPrinter);
        // TODO: 現状では印刷した直後に画面遷移するルートは無いが、もし遷移するならsleep入れたほうがよさそう
      }
      this.stopLoading();

      if (result.success) {
        this.$success(permission.buttonName, result.message);
        if (!!actioned) {
          await actioned();
        }
        // 親に投げる
        this.wfaction();
      } else {
        if (result.isCommentRegistrationFailure && await this.reeditAndDoWorkflowAction(permission, destination, nextMessage, backMessage, shouldPrintDocument, shouldSelectPrinter, actioned, commentKind)) {
          return true;
        }
        this.$error(result.message, result.reason);
        return false;
      }
      return true;
    } catch (e) {
      this.stopLoading();
      this.showRequestErrorMessage("エラー", "処理の実行に失敗しました。", e);
      return false;
    }
  }

  async reeditAndDoWorkflowAction(permission: ButtonInfo, destination: WorkflowDestination, nextMessage: string, backMessage: string, shouldPrintDocument: boolean = false, shouldSelectPrinter: boolean = false, actioned: () => Promise<void> = null, commentKind: ReceptionCommentKinds): Promise<boolean> {
    const message = (commentKind === ReceptionCommentKinds.BackMessage)
      ? backMessage
      : nextMessage;
    const latestComment = await this.getCommentByDetailIdAndCommentType(this.receptionDetailId, ReceptionCommentKindsName.getTypeName(commentKind));
    const editResult = await this.reeditComment(this.labelSetting.nextMessageLabelName + "再編集", this.labelSetting.nextMessageLabelName, message, latestComment);
    if (editResult === undefined) { return false; }

    const editedNextMessage = (commentKind === ReceptionCommentKinds.NextMessage) ? editResult : nextMessage;
    const editedBackMessage = (commentKind === ReceptionCommentKinds.BackMessage) ? editResult : backMessage;
    const latestCommentId = latestComment.deleted ? null : latestComment.id;
    const latestArchiveId = latestComment.deleted ? null : latestComment.archiveId;
    return this.doWorkflowAction(permission, destination, editedNextMessage, editedBackMessage, shouldPrintDocument, shouldSelectPrinter, actioned, commentKind, latestCommentId, latestArchiveId);
  }

  private async printDocumentWithCommitAction(shouldSelectPrinter: boolean = false): Promise<void> {
    const repo = DomainRegistry.document.getDocumentRepository();
    const summary = await repo.getDocumentSummary(this.reception.patientInfo.patient.id, this.document.id);
    const printSource = summary.printSource;
    printSource.hasWatermark = false;
    printSource.withCommitAction = true;
    // TODO: 印刷部数を設定で変更できたほうがよい？
    this.$printPdf(printSource.source, summary.defaultCopies, shouldSelectPrinter);
  }

  async isConfirmedReadable(document: DocumentSummary): Promise<boolean> {
    if (!this.documentShouldConfirm(document)) { return true; }
    const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      this.documentShouldWriteLog(document)
        ? ["この書類の閲覧は許可されていません。閲覧履歴は管理者に通知されます。", "処理を続行しますか？"]
        : ["この書類の閲覧は許可されていません。処理を続行しますか？"],
      "続行する"
    );
    return (index === 0);
  }

  async checkWriteAuditLog(document: DocumentSummary): Promise<void> {
    if (this.documentShouldWriteLog(document)) {
      const repository = DomainRegistry.document.getDocumentRepository();
      try {
        await repository.registerAuditLog(document);
      } catch (e) {
        this.$error(
          "閲覧履歴通知失敗",
          "閲覧履歴通知でエラーが発生しました。",
          e,
        );
        throw e;
      }
    }
  }
}
